/**
 * Build 30-minute pickup/collection time slot options from restaurant openingHours text.
 * Matches patterns like "Monday: 09:00 - 17:00" (see OpeningTimesSection).
 */

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export function formatDateYMD(d) {
	const x = d instanceof Date ? d : new Date(d);
	if (Number.isNaN(x.getTime())) return "";
	const y = x.getFullYear();
	const m = String(x.getMonth() + 1).padStart(2, "0");
	const day = String(x.getDate()).padStart(2, "0");
	return `${y}-${m}-${day}`;
}

function pad2(n) {
	return String(n).padStart(2, "0");
}

export function hhmmToMinutes(hhmm) {
	if (!hhmm || typeof hhmm !== "string") return NaN;
	const m = hhmm.trim().match(/^(\d{1,2}):(\d{2})$/);
	if (!m) return NaN;
	const h = parseInt(m[1], 10);
	const min = parseInt(m[2], 10);
	if (Number.isNaN(h) || Number.isNaN(min)) return NaN;
	return h * 60 + min;
}

export function minutesToHHMM(total) {
	const h = Math.floor(total / 60) % 24;
	const m = total % 60;
	return `${pad2(h)}:${pad2(m)}`;
}

/**
 * Parse openingHours string into [{ day, hours }].
 */
export function parseOpeningHoursEntries(openingHoursString) {
	if (!openingHoursString || openingHoursString === "Hours not available") {
		return [];
	}
	const entries = openingHoursString.split(",").map((e) => e.trim());
	const parsed = [];
	for (const entry of entries) {
		const match = entry.match(/^(\w+):\s*(.+)$/);
		if (match) {
			parsed.push({ day: match[1], hours: match[2].trim() });
		}
	}
	return parsed;
}

/**
 * Extract "HH:MM" / "H:MM" range from a single day's hours text.
 */
export function parseOpenCloseHHMM(hoursText) {
	if (!hoursText || /closed/i.test(hoursText)) return null;
	const m = hoursText.match(/(\d{1,2}:\d{2})\s*[-–]\s*(\d{1,2}:\d{2})/);
	if (!m) return null;
	return { start: m[1], end: m[2] };
}

export function getOpenCloseForDate(pickupDateYmd, openingHoursString) {
	const d = new Date(`${pickupDateYmd}T12:00:00`);
	if (Number.isNaN(d.getTime())) return null;
	const dayName = WEEKDAYS[d.getDay()];
	const entries = parseOpeningHoursEntries(openingHoursString);
	const row = entries.find((e) => e.day === dayName);
	if (!row) return null;
	return parseOpenCloseHHMM(row.hours);
}

/** Default window if hours missing (11:00–22:00). */
const DEFAULT_OPEN = 11 * 60;
const DEFAULT_CLOSE = 22 * 60;

/**
 * @returns {{ value: string, label: string }[]} value = slot start HH:MM (stored as pickupTime)
 */
export function build30MinutePickupSlots({
	openingHoursString,
	pickupDateYmd,
	now = new Date(),
	minLeadMinutesAfterNow = 30,
} = {}) {
	const range = getOpenCloseForDate(pickupDateYmd, openingHoursString);
	let openMin = DEFAULT_OPEN;
	let closeMin = DEFAULT_CLOSE;
	if (range) {
		const o = hhmmToMinutes(range.start);
		const c = hhmmToMinutes(range.end);
		if (!Number.isNaN(o) && !Number.isNaN(c) && c > o) {
			openMin = o;
			closeMin = c;
		}
	}

	const slots = [];
	for (let t = openMin; t + 30 <= closeMin; t += 30) {
		const start = minutesToHHMM(t);
		const end = minutesToHHMM(t + 30);
		slots.push({
			value: start,
			label: `${start} - ${end}`,
		});
	}

	const todayYmd = formatDateYMD(now);
	if (pickupDateYmd === todayYmd && minLeadMinutesAfterNow >= 0) {
		const nowMin = now.getHours() * 60 + now.getMinutes();
		const earliest = Math.ceil(nowMin / 30) * 30 + minLeadMinutesAfterNow;
		return slots.filter((s) => hhmmToMinutes(s.value) >= earliest);
	}
	return slots;
}

/**
 * True if "now" is outside today's published opening window (for info alert).
 */
export function isCollectionOrderType(orderType) {
	const t = String(orderType || "").toLowerCase();
	return t === "pickup" || t === "collection";
}

/**
 * Human-readable slot label for stored pickupTime (e.g. "08:00 - 08:30").
 */
export function formatPickupSlotLabel(pickupTime, { openingHoursString = "", pickupDateYmd } = {}) {
	const start = String(pickupTime || "").trim();
	if (!start) return "";
	const dateYmd = pickupDateYmd || formatDateYMD(new Date());
	const slots = build30MinutePickupSlots({
		openingHoursString,
		pickupDateYmd: dateYmd,
		minLeadMinutesAfterNow: -1,
	});
	const match = slots.find((s) => s.value === start);
	if (match?.label) return match.label;
	const endMin = hhmmToMinutes(start) + 30;
	if (!Number.isNaN(endMin)) {
		return `${start} - ${minutesToHHMM(endMin)}`;
	}
	return start;
}

/**
 * Short date for banner when collection is not today (e.g. "Mon 26 May").
 */
export function formatPickupDateShort(pickupDateYmd, locale = undefined) {
	if (!pickupDateYmd) return "";
	const d = new Date(`${pickupDateYmd}T12:00:00`);
	if (Number.isNaN(d.getTime())) return "";
	const today = formatDateYMD(new Date());
	if (pickupDateYmd === today) return "";
	return d.toLocaleDateString(locale, { weekday: "short", day: "numeric", month: "short" });
}

export function isOutsideOpeningHoursNow(openingHoursString, now = new Date()) {
	const ymd = formatDateYMD(now);
	const entries = parseOpeningHoursEntries(openingHoursString);
	// No schedule text → don't treat as closed (unknown)
	if (!entries.length) return false;
	const range = getOpenCloseForDate(ymd, openingHoursString);
	// Day missing from schedule (unchecked / closed in Business Time) → closed
	if (!range) return true;
	const o = hhmmToMinutes(range.start);
	const c = hhmmToMinutes(range.end);
	if (Number.isNaN(o) || Number.isNaN(c)) return true;
	const nowMin = now.getHours() * 60 + now.getMinutes();
	// Overnight windows (e.g. 22:00–02:00)
	if (c <= o) {
		return !(nowMin >= o || nowMin < c);
	}
	return nowMin < o || nowMin >= c;
}
